/**
 * @voltxampere/web — Inspector collapsible-section persistence
 * (Sprint 26 Lane 10 — S26-INSPECTOR-SECTION-PERSIST)
 *
 * Sprint 25 R5 R5-IA-3 introduced collapsible sections inside
 * <vxa-inspector> (FORMÜL / CANLI ÖLÇÜMLER / DC) defaulting to
 * closed so the form is shorter on first paint. Without
 * persistence, every preset switch / page reload reset the
 * sections back to closed — the user repeatedly re-opened the
 * same sections every time they came back to the inspector.
 *
 * This store mirrors `sim-prefs.ts`:
 *   - signal-driven (`@preact/signals-core`) read surface
 *   - lazy IDB hydrate (idempotent, gated by `initialised`)
 *   - debounced write on each toggle (500 ms — F-IA gate)
 *   - test-only `__resetInspectorPrefsForTests()` for
 *     deterministic afterEach cleanup
 *
 * The persisted shape is `inspectorOpenSections: readonly
 * string[]` on the canonical settings row (Sprint 23 ADR-0047).
 * Empty list means "use static defaults" = all sections closed.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/**
 * Canonical inspector section ids. Adding a new collapsible
 * section to the inspector means adding its id here AND wiring
 * the matching `<details data-section-id>` markup. Every id
 * must round-trip through IDB unchanged, so they are stable
 * string literals (NOT i18n keys).
 */
export const INSPECTOR_SECTION_IDS = ["formula", "measurements", "thermal", "dc"] as const;
export type InspectorSectionId = (typeof INSPECTOR_SECTION_IDS)[number];

/**
 * Default open state — every section closed. Matches Sprint 25
 * R5-IA-3 static markup defaults so a fresh-install user sees
 * the same compact inspector they did pre-Lane-10.
 */
function isKnownSectionId(value: string): value is InspectorSectionId {
  return (INSPECTOR_SECTION_IDS as readonly string[]).includes(value);
}

/** Live signal: ids of currently-open sections. Set semantics. */
export const inspectorOpenSections = signal<readonly InspectorSectionId[]>([]);

/** True when an id is currently open. Read-only helper for views. */
export function isInspectorSectionOpen(id: InspectorSectionId): boolean {
  return inspectorOpenSections.value.includes(id);
}

let initialised = false;
let writeTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Boot path — read the settings row and hydrate the open-set
 * signal. Idempotent; tests reset via
 * `__resetInspectorPrefsForTests()`.
 */
export async function initInspectorPrefs(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    const raw = settings?.inspectorOpenSections;
    if (Array.isArray(raw)) {
      // Defensive shape filter: drop unknown ids (e.g. a stored
      // section that has been removed in a later sprint) so the
      // UI never tries to open a section that no longer exists.
      const filtered = raw.filter(
        (v): v is InspectorSectionId => typeof v === "string" && isKnownSectionId(v),
      );
      inspectorOpenSections.value = uniqueOrdered(filtered);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA inspector-prefs] hydrate failed:", err);
    }
  }
}

/**
 * Mutator. `open === true` adds the id to the set; `open === false`
 * removes it. The IDB write is debounced 500 ms so a rapid
 * fold-then-unfold does not produce two writes back-to-back.
 */
export function setInspectorSectionOpen(id: InspectorSectionId, open: boolean): void {
  const current = inspectorOpenSections.value;
  const has = current.includes(id);
  if (open && !has) {
    inspectorOpenSections.value = uniqueOrdered([...current, id]);
  } else if (!open && has) {
    inspectorOpenSections.value = current.filter((v) => v !== id);
  } else {
    return;
  }
  scheduleWrite();
}

/**
 * Convenience — flip the open state of a section id. Used by the
 * inspector's `<details>` toggle handler when only the new state
 * is known via `event.target.open`.
 */
export function toggleInspectorSection(id: InspectorSectionId): void {
  setInspectorSectionOpen(id, !isInspectorSectionOpen(id));
}

const DEBOUNCE_MS = 500;

function scheduleWrite(): void {
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
  }
  writeTimer = setTimeout(() => {
    writeTimer = null;
    void mergeSettings({ inspectorOpenSections: inspectorOpenSections.value }).catch(
      (err: unknown) => {
        if (import.meta.env.DEV) {
          console.warn("[VXA inspector-prefs] persist failed:", err);
        }
      },
    );
  }, DEBOUNCE_MS);
}

function uniqueOrdered(values: readonly InspectorSectionId[]): readonly InspectorSectionId[] {
  const seen = new Set<InspectorSectionId>();
  const out: InspectorSectionId[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Test-only flush. Runs the pending debounce timer immediately so
 * tests can assert the IDB write happened without sleeping for the
 * full 500 ms. Returns a Promise that resolves once the merge
 * settles (or rejects if the merge throws).
 */
export async function __flushInspectorPrefsForTests(): Promise<void> {
  if (writeTimer === null) return;
  clearTimeout(writeTimer);
  writeTimer = null;
  await mergeSettings({ inspectorOpenSections: inspectorOpenSections.value });
}

/** Test-only reset — wipe signal + clear pending timer + force re-init. */
export function __resetInspectorPrefsForTests(): void {
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  inspectorOpenSections.value = [];
  initialised = false;
}
